import React, { Component } from "react"
import { ACT_BREAK_TIME_IN_SECONDS, ACT_SECTION_MODULE_KEYS, getTestBreakTimeACT, populateEnhancedActPracticeTestQuestions, saveTestStartTime, TestTimeIndicator } from "../../../../services/utils/practice-test-util"
import { SimpleTimer } from "../../../../services/utils/simple-timer"
import { Context, Resource, sendUserActivity, UserActivityEx } from "../../../../services/liveview/liveview-service"
import WelcomePage from "./WelcomePage"
import ComputerTestACT from "./ComputerTestACT"
import ModuleOverPage from "./pages/ModuleOverPage"
import BreakInfoPage from "./pages/BreakInfoPage"
import TestOverPage from "./pages/TestOverPage"

/**
 * ACT Workflow:
 *  + Welcome
 *  + Start
 *  + Time select: Regular, 1.5x, 2x, 3x
 *  + English
 *  + Break
 *    . 10 min countdown
 *    . Can click resume to continue
 *  + Math
 *  + Break
 *      . 10 min countdown
 *      . Can click resume to continue
 *  + Reading
 *  + Question if the user wants to continue Science now or later?
 *  + Science (could be taken later)
 *  + Ending Page
 *      . Message
 *      . Take student to the Enhanced ACT Dashboard
 * 
 */

interface Props {
    userInfo: any
    testDetails: any
    onTestExit: (testName: string) => void
}

interface State {
    currentStep: number
    steps: any[]
    testTimeIndicator: TestTimeIndicator
}

export default class WorkflowACT extends Component<Props, State> {
    simpleTimer = new SimpleTimer()

    constructor(props) {
        super(props)
        this.state = {
            currentStep: 0,
            steps: [],
            testTimeIndicator: "regular",
        }
    }

    componentDidMount(): void {
        this.initializeSteps()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.testDetails !== this.props.testDetails) {
            this.initializeSteps()
        }
    }

    componentWillUnmount() {

    }

    /**
     * In the Computer-Based Virtaul Practice Tests a user can exit and resume the test many times
     * so we need to allow multiple entries for this resource, hence the sendUseractivity has 
     * a boolean flag set to true.
     */
    sendStats(args?: {start?: boolean, end?: boolean, moduleCategory?: string}) {
        let {testDetails} = this.props
        let {testName} = testDetails
        let seconds = this.simpleTimer.diffInSeconds()
        let ended = seconds
        let category = "PracticeTestACT"
        let context = Context.COMPUTER_TEST
        let contextId = `${testName}`
        let resource = Resource.TEST_SESSION
        let resourceId = `${testName}`
        let userActivityEx: UserActivityEx = {
            context, contextId, 
            resource, resourceId, 
            category,
            ended
        }

        if (args) {
            let {start=false, end=false, moduleCategory} = args
            if (start) {
                userActivityEx = {...userActivityEx, resource: Resource.START}
            }
            else if (end) {
                userActivityEx = {...userActivityEx, resource: Resource.END}
            }
            if (moduleCategory) {
                userActivityEx = {...userActivityEx, category: moduleCategory}
            }
        }

        sendUserActivity(userActivityEx, true)
        this.simpleTimer.reset()
    }

    handleNext = () => {
        let {currentStep} = this.state
        ++currentStep
        this.setState({currentStep})
    }

    handleTestTimeSelection = (testTimeIndicator: TestTimeIndicator) => {
        let {currentStep} = this.state
        let {testDetails} = this.props

        testDetails.testTimeIndicator = testTimeIndicator

        ++currentStep
        this.setState({currentStep, testTimeIndicator})
    }

    handleTestExit = () => {
        let {onTestExit} = this.props
        let {testDetails} = this.props
        let {testName} = testDetails
        onTestExit(testName)
    }

    /**
     * Will find the correct starting steps based on the current module
     * @param currentModuleNameKey 
     * @returns 
     */
    findStartingStepBasedOnModuleName = (currentModuleNameKey) => {
        let startingStep = 0
        switch (currentModuleNameKey) {
            case ACT_SECTION_MODULE_KEYS.ENGLISH_MODULE:
                startingStep = 1
                break

            case ACT_SECTION_MODULE_KEYS.MATH_MODULE:
                startingStep = 3
                break

            case ACT_SECTION_MODULE_KEYS.READING_MODULE:
                startingStep = 6
                break

            case ACT_SECTION_MODULE_KEYS.SCIENCE_MODULE:
                startingStep = 8
                break
        }
        return startingStep
    }

    initializeSteps = () => {
        let {userInfo, testDetails } = this.props

        if (!testDetails) return

        testDetails = populateEnhancedActPracticeTestQuestions(testDetails)

        let {displayName, currentModuleNameKey} = testDetails
        let currentStep = 0
        let breakDurationInSeconds = getTestBreakTimeACT(testDetails)

        // If the test has already started then find the correct step to continue!
        if (currentModuleNameKey) {
            currentStep = this.findStartingStepBasedOnModuleName(currentModuleNameKey)
        }
        else {
            // Test starting...
            this.submitTestStart(testDetails)
        }

        let steps = [
            // Step 0
            <WelcomePage title={displayName} onTimeSelected={this.handleTestTimeSelection} />,

            // Step 1: English Test
            <ComputerTestACT
                userInfo={userInfo}
                testDetails={testDetails}
                onNext={this.handleNext}
                onTestExit={this.handleTestExit}
                currentModuleNameKey={ACT_SECTION_MODULE_KEYS.ENGLISH_MODULE}
                onPageUnmount={() => this.sendStats({moduleCategory: ACT_SECTION_MODULE_KEYS.ENGLISH_MODULE})}
            />,

            // Step 2
            <ModuleOverPage title={"English"} onNext={this.handleNext} />,

            // Step 3: Math Test
            <ComputerTestACT 
                userInfo={userInfo}
                testDetails={testDetails}
                onNext={this.handleNext}
                onTestExit={this.handleTestExit}
                currentModuleNameKey={ACT_SECTION_MODULE_KEYS.MATH_MODULE}
                onPageUnmount={() => this.sendStats({moduleCategory: ACT_SECTION_MODULE_KEYS.MATH_MODULE})}
            />,

            // Step 4
            <ModuleOverPage title={"Math"} onNext={this.handleNext} />,

            // Step 5: Main break
            <BreakInfoPage userInfo={userInfo} durationInSeconds={breakDurationInSeconds} onNext={this.handleNext} />,

            // Step 6: Reading
            <ComputerTestACT 
                userInfo={userInfo}
                testDetails={testDetails}
                onNext={this.handleNext}
                onTestExit={this.handleTestExit}
                currentModuleNameKey={ACT_SECTION_MODULE_KEYS.READING_MODULE}
                onPageUnmount={() => this.sendStats({moduleCategory: ACT_SECTION_MODULE_KEYS.READING_MODULE})}
            />,

            // Step 7
            <ModuleOverPage title={"Reading"} onNext={this.handleNext} />,

            // Step 8: Science (Optional)
            <ComputerTestACT 
                userInfo={userInfo}
                testDetails={testDetails}
                onNext={this.handleNext}
                onTestExit={this.handleTestExit}
                currentModuleNameKey={ACT_SECTION_MODULE_KEYS.SCIENCE_MODULE}
                onPageUnmount={() => this.sendStats({moduleCategory: ACT_SECTION_MODULE_KEYS.SCIENCE_MODULE})}
            />,

            // Step 9
            <TestOverPage title={displayName} onPageShown={this.onTestOverPageShown} onNext={this.handleTestExit} />,
        ]

        this.setState({currentStep, steps})
    }

    submitTestStart = (testDetails) => {
        testDetails.startedTime = new Date().toISOString()
        saveTestStartTime(testDetails)
        this.sendStats({start: true})
    }

    onTestOverPageShown = () => {
        // Computer-based Practice Test is finished!
        setTimeout(() => {
            this.sendStats({end: true})
        }, 300)
    }

    render() {
        let {steps, currentStep} = this.state

        if (!steps || steps.length === 0) return null

        return (
            <>
                {steps[currentStep]}
            </>
        )
    }
}

